import { useNavigate } from '@tanstack/react-router';
import { Box, Stack, UnstyledButton, Group, Text, Switch, rem } from '@mantine/core';
import { useState, useEffect, useMemo } from 'react';
import type { KeyboardEvent } from 'react';
import { msalInstance } from '../../../../api/auth';
import { trackComponentInteraction } from '../../../../utils/analytics';
import classes from './ProfileModal.module.css';
import { useColorScheme } from '../../../../hooks/useColorScheme';
import ToggleSunIcon from '../../../../assets/icons/toggle-sun.svg?react';
import ToggleMoonIcon from '../../../../assets/icons/toggle-moon.svg?react';

// =============================================================================
// TYPES
// =============================================================================

export interface ProfileModalProps {
  onClose?: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const USER_GUIDE_URL = import.meta.env.VITE_APP_USER_GUIDE_URL;

const RELEASE_NOTES_URL = import.meta.env.VITE_APP_RELEASE_NOTES_URL;

const CO_OP_GPT_BETA_URL = import.meta.env.VITE_APP_COOPGPT_BETA_URL;

const CO_OP_GPT_PROD_URL = import.meta.env.VITE_APP_COOPGPT_PROD_URL;

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ProfileModal = ({ onClose }: ProfileModalProps) => {
  const [hasAdminAccess, setHasAdminAccess] = useState<boolean>(false);
  const { toggleColorScheme, isDark } = useColorScheme();
  const navigate = useNavigate();

  const isProdEnvironment = useMemo(() => {
    return window.location.origin === new URL(CO_OP_GPT_PROD_URL).origin;
  }, []);
  const isBetaEnvironment = useMemo(() => {
    return window.location.origin === new URL(CO_OP_GPT_BETA_URL).origin;
  }, []);
  const targetEnvironmentURL = isProdEnvironment ? CO_OP_GPT_BETA_URL : CO_OP_GPT_PROD_URL;
  const targetEnvironment = isProdEnvironment ? "Beta" : "Production";


  useEffect(() => {
    const checkRoles = () => {
      const account = msalInstance.getActiveAccount();
      if (account) {
        const roles = account.idTokenClaims?.roles || [];
        setHasAdminAccess(roles.includes('Manage.All'));
      }
    };
    checkRoles();
  }, []);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const handleUserGuideClick = () => {
    trackComponentInteraction(
      'ProfileModal',
      'User Guide',
      'link',
      'click',
      'user_guide_access',
      USER_GUIDE_URL
    );
    window.open(USER_GUIDE_URL, '_blank', 'noopener,noreferrer');
    onClose?.();
  };

  const handleReleaseNotesClick = () => {
    trackComponentInteraction(
      'ProfileModal',
      'Release Notes',
      'link',
      'click',
      'release_notes_access',
      RELEASE_NOTES_URL
    );
    window.open(RELEASE_NOTES_URL, '_blank', 'noopener,noreferrer');
    onClose?.();
  };

  const handleEnvironmentSwitch = () => {
    trackComponentInteraction(
      'ProfileModal',
      'Co-op GPT ' + targetEnvironment,
      'link',
      'click',
      `Co-op GPT ${targetEnvironment} access`,
      targetEnvironmentURL
    );
    window.location.href = targetEnvironmentURL;
    onClose?.();
  }

  const handleAdminClick = () => {
    // const adminUrl = 'https://app.powerbi.com/groups/b0f24f79-95b6-4827-8841-f1c7cd298fd7/reports/2a2b38fb-5adb-4fa9-93fd-dd1ca97b0988?experience=power-bi';
    trackComponentInteraction(
      'ProfileModal',
      'Admin',
      'link',
      'click',
      'admin_access',
      '/admin'
    );
    // Open Power BI report in new window
    navigate({ to: '/admin' });
    onClose?.();
  };

  const toggleIcon = useMemo(
    () =>
      isDark ? (
        <ToggleMoonIcon className={classes.toggleIcon} />
      ) : (
        <ToggleSunIcon className={classes.toggleIcon} />
      ),
    [isDark]
  );

  const handleDarkModeToggle = () => {
    trackComponentInteraction(
      'ProfileModal',
      'Dark Mode',
      'toggle',
      'click',
      isDark ? 'light' : 'dark'
    );
    toggleColorScheme();
  };

  const handleDarkModeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDarkModeToggle();
    }
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <Box className={classes.modal}>
      <Stack gap={0}>
        <UnstyledButton
          onClick={handleUserGuideClick}
          className={classes.modalItem}
        >
          <Group justify="space-between" style={{ width: '100%' }}>
            <Text className={classes.itemText}>User Guide</Text>
          </Group>
        </UnstyledButton>

        <UnstyledButton
          onClick={handleReleaseNotesClick}
          className={classes.modalItem}
        >
          <Group justify="space-between" style={{ width: '100%' }}>
            <Text className={classes.itemText}>Release Notes</Text>
            <Text className={classes.versionText}>{import.meta.env.VITE_APP_VERSION}</Text>
          </Group>
        </UnstyledButton>
        {(isProdEnvironment || isBetaEnvironment) &&
          <UnstyledButton
            onClick={handleEnvironmentSwitch}
            className={classes.modalItem}
          >
            <Group justify="space-between" style={{ width: '100%' }}>
              <Text className={classes.itemText}>{'Co-op GPT ' + targetEnvironment}</Text>
            </Group>
          </UnstyledButton>
        }
        <div
          className={classes.toggleRow}
          onClick={handleDarkModeToggle}
          role="switch"
          tabIndex={0}
          aria-checked={isDark}
          onKeyDown={handleDarkModeKeyDown}
        >
          <Text className={classes.itemText}>Dark Mode</Text>
          <Switch
            checked={isDark}
            aria-label="Toggle dark mode"
            aria-hidden="true"
            tabIndex={-1}
            readOnly
            size="md"
            classNames={{
              root: classes.toggleRoot,
            }}
            styles={(_, { checked }) => ({
              track: {
                width: rem(48),
                height: rem(24),
                borderRadius: rem(32),
                border: 'none',
                backgroundColor: checked
                  ? 'var(--fonterra-toggle-track-on)'
                  : 'var(--fonterra-toggle-track-off)',
              },
              thumb: {
                width: rem(18),
                height: rem(18),
                backgroundColor: 'var(--fonterra-toggle-thumb)',
                color: checked
                  ? 'var(--fonterra-toggle-icon-on, var(--fonterra-toggle-icon))'
                  : 'var(--fonterra-toggle-icon-off, var(--fonterra-toggle-icon))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            })}
            thumbIcon={toggleIcon}
          />

        </div>

        {hasAdminAccess && (
          <>
            <Box className={classes.divider} />

            <UnstyledButton
              onClick={handleAdminClick}
              className={classes.modalItem}
            >
              <Group justify="space-between" style={{ width: '100%' }}>
                <Text className={classes.itemText}>Admin Options</Text>
              </Group>
            </UnstyledButton>
          </>
        )}
      </Stack>
    </Box>
  );
};
